import { FormEvent, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { FormBlocksContainer, TrainerBlock } from "../styles";
import { TrainerInfoBlock } from "./TrainerInfoBlock";
import { TrainerImagesBlock } from "./TrainerImagesBlock";
import { TrainerBundlesBlock } from "./TrainerBundlesBlock";
import { TrainerFormButtons } from "./TrainerFormButtons";
import { LoadingProgressOverlay } from "../../shared/LoadingProgress/LoadingProgressOverlay";
import { ErrorMessageSnackbar } from "../../shared/ErrorMessageSnackbar/ErrorMessageSnackbar";
import { SuccessSnackbar } from "../../shared/SuccessSnackbar/SuccessSnackbar";
import { TrainerContext } from "../context";
import { CreateDraftTrainerMutationVariables, GetDraftTrainerDocument, GetDraftTrainersDocument, Trainer, useCreateDraftTrainerMutation, usePublishTrainerMutation, useUpdateDraftTrainerMutation } from "../../../generated/graphql";

export const TrainerForm = () => {
    const { push } = useHistory();
    const { appId, trainerId } = useParams<{ appId: string, trainerId: string }>();
    const [notificationStatus, setNotificationStatus] = useState({
        error: false,
        success: false,
        message: "",
    });

    const [createTrainer, { loading: createTrainerLoading }] = useCreateDraftTrainerMutation();
    const [updateTrainer, { loading: updateTrainerLoading }] = useUpdateDraftTrainerMutation();
    const [publishTrainer, { loading: publishTrainerLoading }] = usePublishTrainerMutation();

    const checkFile = (value): File | undefined => {
        return value && value instanceof File ? value : undefined;
    };
    
    const handleSubmit = async (e: FormEvent<HTMLFormElement>, data: Trainer) => {
        e.preventDefault();
        if (trainerId) {
            await updateTrainer({
                variables: {
                    updateDraftTrainerId: trainerId,
                    name: data.name?.trim(),
                    description: data.description?.trim(),
                    dictionaries: data.dictionaries?.trim(),
                    accessType: data.accessType || undefined,
                    price: data.price || 0,
                    previewImage: checkFile(data.previewImage),
                    webpGifImage: checkFile(data.webpGifImage),
                    mp4GifImage: checkFile(data.mp4GifImage),
                    androidBundle: checkFile(data.androidBundle),
                    androidManifest: checkFile(data.androidManifest),
                    iosBundle: checkFile(data.iosBundle),
                    iosManifest: checkFile(data.iosManifest),
                    webGlBundle: checkFile(data.webGlBundle),
                    webGlManifest: checkFile(data.webGlManifest),
                    isForWeb: data.isForWeb,
                },
                onCompleted: (data) => {
                    if (data.updateDraftTrainer) { 
                        setNotificationStatus({ ...notificationStatus, success: true, message: "Тренажер успішно збережено" });
                        return;
                    }
                    setNotificationStatus({ ...notificationStatus, error: true, message: "Сталась помилка при збереженні тренажера" });
                },
                onError: () => {
                    setNotificationStatus({ ...notificationStatus, error: true, message: "Сталась помилка при збереженні тренажера" });
                },
                awaitRefetchQueries: true,
                refetchQueries: [GetDraftTrainersDocument, GetDraftTrainerDocument],
            });
        } else {
            await createTrainer({
                variables: {
                    ...data as CreateDraftTrainerMutationVariables,
                    name: data.name || "Назва тренажера",
                },
                onCompleted: (data) => {
                    if (data.createDraftTrainer) { 
                        setNotificationStatus({ ...notificationStatus, success: true, message: "Тренажер успішно створено" });
                        setTimeout(() => {push(`/mobileApps/${appId}/trainers`)}, 1000);
                        return;
                    }
                    setNotificationStatus({ ...notificationStatus, error: true, message: "Сталась помилка при створенні тренажера" });
                },
                onError: () => {
                    setNotificationStatus({ ...notificationStatus, error: true, message: "Сталась помилка при створенні тренажера" });
                },
                awaitRefetchQueries: true,
                refetchQueries: [GetDraftTrainersDocument, GetDraftTrainerDocument],
            });
        }
    };

    const handlePublish = async () => { 
        await publishTrainer({
            variables: {
                publishTrainerId: trainerId,
                isPublish: true,
            },
            onCompleted: (data) => {
                if (data.publishTrainer) { 
                    setNotificationStatus({ ...notificationStatus, success: true, message: "Тренажер успішно опубліковано" });
                    return;
                }
                setNotificationStatus({ ...notificationStatus, error: true, message: "Сталась помилка при публікації тренажера" });
            },
            onError: () => {
                setNotificationStatus({ ...notificationStatus, error: true, message: "Сталась помилка при публікації тренажера" }); 
            },
            awaitRefetchQueries: true,
            refetchQueries: [GetDraftTrainersDocument, GetDraftTrainerDocument],
        });
    };

    const handleDeleteMediaFile = async (key: string) => {
        if (trainerId) {
            await updateTrainer({
                variables: {
                    updateDraftTrainerId: trainerId,
                    [key]: null,
                },
                onCompleted: (data) => {
                    if (data.updateDraftTrainer) { 
                        setNotificationStatus({ ...notificationStatus, success: true, message: "Файл успішно видалено" });
                        return;
                    }
                    setNotificationStatus({ ...notificationStatus, error: true, message: "Сталась помилка при видаленні файлу" });
                },
                onError: () => {
                    setNotificationStatus({ ...notificationStatus, error: true, message: "Сталась помилка при видаленні файлу" });
                },
                awaitRefetchQueries: true,
                refetchQueries: [GetDraftTrainersDocument, GetDraftTrainerDocument],
            });
        };
    };

    
    return (
        <TrainerContext.Consumer>
            {({ data }) => (
                <>
                    <form onSubmit={(e) => handleSubmit(e, data)}>
                        <FormBlocksContainer>
                            <TrainerBlock>
                                <TrainerInfoBlock />
                                <TrainerImagesBlock onDeleteFile={handleDeleteMediaFile} />
                            </TrainerBlock>
                            <TrainerBundlesBlock onDeleteFile={handleDeleteMediaFile} />
                        </FormBlocksContainer>
                        <TrainerFormButtons isPublished={data.isPublished} canPublish={data.canPublish} onPublish={handlePublish} />
                    </form>
                    <p>* - Тренажер може бути збережений без публікації. Щоб опублікувати тренажер, необхідно заповнити всі обов'язкові поля із зірочкою</p>
                    <SuccessSnackbar
                        open={notificationStatus.success}
                        handleClose={() => setNotificationStatus({ ...notificationStatus, success: false })}
                        message={notificationStatus.message}
                        snackbarProps={{
                            anchorOrigin: { vertical: "bottom", horizontal: "center" },
                        }}
                    />
                    <ErrorMessageSnackbar
                        open={notificationStatus.error}
                        message={notificationStatus.message}
                        closeHandler={() => setNotificationStatus({ ...notificationStatus, error: false })}
                    />
                    {(createTrainerLoading || updateTrainerLoading || publishTrainerLoading) && <LoadingProgressOverlay />}
                </>
            )}
        </TrainerContext.Consumer>
    );
};
